import { useTranslation } from 'react-i18next';
import { BillingDocument } from '@/hooks/useBillingDocuments';
import { Card } from '@/components/ui/card';
import { TrendingUp, CheckCircle, Clock, Euro } from 'lucide-react';

interface BillingStatsCardProps {
  documents: BillingDocument[];
}

export const BillingStatsCard = ({ documents }: BillingStatsCardProps) => {
  const { t } = useTranslation();

  // Calculate statistics
  const totalInvoiced = documents.reduce((sum, doc) => sum + Number(doc.total_amount), 0);
  const totalPaid = documents
    .filter(doc => doc.status === 'paid')
    .reduce((sum, doc) => sum + Number(doc.total_amount), 0);
  const totalPending = documents
    .filter(doc => doc.status === 'sent')
    .reduce((sum, doc) => sum + Number(doc.total_amount), 0);
  const paidPercentage = totalInvoiced > 0 ? Math.round((totalPaid / totalInvoiced) * 100) : 0;

  const stats = [
    {
      label: t('billing.stats.totalInvoiced'),
      value: totalInvoiced,
      icon: Euro,
      color: '#37699E',
      bgColor: 'bg-[#37699E]/10',
    },
    {
      label: t('billing.stats.totalPaid'),
      value: totalPaid,
      icon: CheckCircle,
      color: '#14A38B',
      bgColor: 'bg-[#14A38B]/10',
    },
    {
      label: t('billing.stats.totalPending'),
      value: totalPending,
      icon: Clock,
      color: '#F2AC57',
      bgColor: 'bg-[#F2AC57]/10',
    },
  ];

  return (
    <Card className="bg-white border-[#DBE2EA] p-6 mb-6">
      <div className="flex items-center gap-2 mb-6">
        <TrendingUp className="text-[#0880AE]" size={24} />
        <h2 className="text-xl font-semibold text-[#37699E] font-['Lexend_Deca']">
          {t('billing.stats.title')}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`${stat.bgColor} p-2 rounded-lg`}>
                  <Icon size={20} style={{ color: stat.color }} />
                </div>
                <span className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                  {stat.label}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-semibold font-['Lexend_Deca']" style={{ color: stat.color }}>
                  {stat.value.toFixed(2)}
                </span>
                <span className="text-lg text-[#5A6B7A]">€</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment progress bar */}
      {totalInvoiced > 0 && (
        <div className="mt-6 pt-6 border-t border-[#DBE2EA]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#5A6B7A]">
              {t('billing.stats.paymentProgress')}
            </span>
            <span className="text-sm font-semibold text-[#14A38B]">
              {paidPercentage}%
            </span>
          </div>
          <div className="w-full h-2 bg-[#DBE2EA] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#14A38B] rounded-full transition-all duration-300"
              style={{ width: `${paidPercentage}%` }}
            />
          </div>
        </div>
      )}
    </Card>
  );
};
